import React, { useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { ChefHat, ShoppingCart, Clock, Heart } from 'lucide-react';
import { Events } from '../../client/analytics/events';
import { trackEvent } from '../config/analytics';

interface HowItWorksModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function HowItWorksModal({ isOpen, onClose }: HowItWorksModalProps) {
  // Track modal open
  useEffect(() => {
    if (isOpen) {
      try { trackEvent(Events.HowItWorksOpened) } catch {}
    }
  }, [isOpen]);

  const handleCTAClick = () => {
    try { trackEvent(Events.HowItWorksCTAClick) } catch {}
    onClose();
  };
  const steps = [
    {
      icon: <ChefHat className="w-8 h-8 text-coral-600" />,
      title: "Tell us about yourself",
      description: "Share your dietary preferences, cooking skills, and household size to get personalized recommendations."
    },
    {
      icon: <Clock className="w-8 h-8 text-coral-600" />,
      title: "Get your meal plan",
      description: "Our AI chef creates a weekly meal plan with recipes tailored to your tastes, schedule, and budget."
    },
    {
      icon: <ShoppingCart className="w-8 h-8 text-coral-600" />,
      title: "Shop smart",
      description: "Get a consolidated shopping list organized by store section. No more wandering the aisles!"
    },
    {
      icon: <Heart className="w-8 h-8 text-coral-600" />,
      title: "Cook with confidence",
      description: "Follow step-by-step recipes designed for your skill level. Save favorites and swap recipes anytime."
    }
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-dusk text-center">
            How Nura Works
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 mt-6">
          <p className="text-center text-supporting text-lg">
            From your preferences to your plate in 4 simple steps
          </p>
          
          <div className="grid gap-6">
            {steps.map((step, index) => (
              <Card key={index} className="border-l-4 border-l-spice-600">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-coral-50 rounded-full flex items-center justify-center">
                        {step.icon}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-dusk mb-2">
                        {index + 1}. {step.title}
                      </h3>
                      <p className="text-supporting">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="bg-coral-50 rounded-lg p-6 text-center">
            <h4 className="font-semibold text-dusk mb-2">Ready to start cooking smarter?</h4>
            <p className="text-supporting mb-4">
              Join thousands of home cooks who have simplified their meal planning with Nura.
            </p>
            <Button 
              onClick={handleCTAClick}
              className="bg-coral-600 hover:bg-coral-700 text-white"
            >
              Get Started Now
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}